import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, ArrowLeft, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useMessaging } from '@/hooks/useMessaging';
import { useAuth } from '@/hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface MessagingModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialUserId?: string;
  initialUserName?: string;
}

export function MessagingModal({ isOpen, onClose, initialUserId, initialUserName }: MessagingModalProps) {
  const { user } = useAuth();
  const { 
    conversations, 
    messages, 
    loading,
    activeConversation,
    fetchMessages,
    sendMessage,
    startConversation,
    setActiveConversation 
  } = useMessaging();
  
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialUserId && isOpen) {
      startConversation(initialUserId);
    }
  }, [initialUserId, isOpen, startConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!activeConversation || !newMessage.trim()) return;
    
    setSending(true);
    await sendMessage(activeConversation, newMessage);
    setNewMessage('');
    setSending(false);
  };

  const handleBack = () => {
    setActiveConversation(null);
  };

  const activeConvo = conversations.find(c => 
    c.other_user.id === activeConversation
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-card w-full max-w-md h-[70vh] rounded-2xl overflow-hidden flex flex-col shadow-2xl border border-border"
        >
          {/* Header */}
          <div className="p-4 border-b border-border flex items-center gap-3 bg-muted/30">
            {activeConversation ? (
              <>
                <Button variant="ghost" size="icon" onClick={handleBack}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <Avatar className="w-10 h-10">
                  <AvatarImage src={activeConvo?.other_user.avatar_url || initialUserName?.[0]} />
                  <AvatarFallback className="bg-primary/20 text-primary">
                    {(activeConvo?.other_user.display_name || initialUserName)?.[0]?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-semibold text-foreground">
                    {activeConvo?.other_user.display_name || initialUserName || 'User'}
                  </p>
                </div>
              </>
            ) : (
              <>
                <MessageCircle className="w-6 h-6 text-primary" />
                <h2 className="text-lg font-bold text-foreground flex-1">Messages</h2>
              </>
            )}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {activeConversation ? (
              <div className="flex flex-col h-full">
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-3">
                    {messages.length === 0 ? (
                      <div className="text-center text-muted-foreground py-8">
                        <p>Start a conversation!</p>
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={cn(
                            "flex",
                            msg.sender_id === user?.id ? "justify-end" : "justify-start"
                          )}
                        >
                          <div
                            className={cn(
                              "max-w-[75%] px-4 py-2 rounded-2xl",
                              msg.sender_id === user?.id
                                ? "bg-primary text-primary-foreground rounded-br-md"
                                : "bg-muted text-foreground rounded-bl-md"
                            )}
                          >
                            <p className="text-sm">{msg.content}</p>
                            <p className={cn(
                              "text-xs mt-1 opacity-70",
                              msg.sender_id === user?.id ? "text-right" : "text-left"
                            )}>
                              {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Input */}
                <div className="p-4 border-t border-border bg-background">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleSend} 
                      disabled={!newMessage.trim() || sending}
                      size="icon"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <ScrollArea className="h-full">
                {loading ? (
                  <div className="flex items-center justify-center h-40">
                    <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No conversations yet</p>
                    <p className="text-sm text-muted-foreground/70 mt-2">
                      Follow someone and they follow you back to start messaging
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => fetchMessages(conv.other_user.id)}
                        className="w-full p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
                      >
                        <div className="relative">
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={conv.other_user.avatar_url || undefined} />
                            <AvatarFallback className="bg-primary/20 text-primary">
                              {conv.other_user.display_name?.[0]?.toUpperCase() || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          {conv.unread_count > 0 && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground truncate">
                            {conv.other_user.display_name || 'User'}
                          </p>
                          <p className="text-sm text-muted-foreground truncate">
                            {conv.last_message || 'No messages yet'}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: true })}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
